import { Link } from "react-router-dom";
import { ArrowRight, PlayCircle, Camera, User, FileText, Wand2, Upload, Image as ImageIcon } from "lucide-react";

const steps = [
  { num: "01", title: "Upload reference photos", body: "Pick 5–10 clear, recent photos of your face. Front-on, 3/4, profile. Window light is your friend.", cta: "Go to Step 1: Upload", to: "/app/step1", icon: Camera },
  { num: "02", title: "Fill in your brand profile", body: "Industry, style, wardrobe, hair, skin tone. The more specific, the more your prompts will sound like you.", cta: "Go to Step 2: Brand Profile", to: "/app/step2", icon: User },
  { num: "03", title: "Pick a prompt from the library", body: "Studio headshots, lifestyle, industry-specific, social. Copy the one you want and customise the brackets.", cta: "Go to Step 3: Prompts", to: "/app/step3", icon: FileText },
  { num: "04", title: "Generate in ChatGPT", body: "Paste your prompt + reference photos into ChatGPT (Plus). Iterate 2–3 times until results feel right.", cta: "Go to Step 4: Generate", to: "/app/step4", icon: Wand2 },
  { num: "05", title: "Upload your results", body: "Drop your favourites into your gallery. Add tags so you can find them later.", cta: "Go to Step 5: Upload", to: "/app/step5", icon: Upload },
  { num: "06", title: "Use them everywhere", body: "Website, LinkedIn, Instagram, podcasts. Mark your top picks as favourites for quick access.", cta: "Go to Step 6: Gallery", to: "/app/step6", icon: ImageIcon },
];

export default function PhotoSystem() {
  return (
    <div className="max-w-5xl mx-auto" data-testid="photo-system-page">
      <span className="eyebrow">The system</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-3">The Photo System</h1>
      <p className="text-lg text-[#52525b] mt-3 max-w-2xl">Six steps. Bookmark this page — it's your map.</p>

      <div className="mt-8 bsp-card p-6 flex items-center gap-5 bg-[#1A1F3D] text-white" data-testid="walkthrough-card">
        <div className="w-12 h-12 rounded-xl bg-[#D4A853]/20 flex items-center justify-center">
          <PlayCircle className="w-6 h-6 text-[#D4A853]" />
        </div>
        <div className="flex-1">
          <div className="font-display text-xl">Walkthrough video</div>
          <div className="text-white/70 text-sm">A 4-minute tour of the whole process before you upload a single photo.</div>
        </div>
        <Link to="/app/resources" className="px-4 py-2 rounded-lg bg-[#D4A853] text-[#1A1F3D] font-semibold text-sm inline-flex items-center gap-2">
          Watch <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      <div className="mt-10 space-y-5">
        {steps.map((s, i) => (
          <div key={i} className="bsp-card bsp-card-hover p-6 md:p-8 grid md:grid-cols-12 gap-6 items-start" data-testid={`system-step-${i + 1}`}>
            <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#F5E6E0] flex items-center justify-center">
                <s.icon className="w-5 h-5 text-[#1A1F3D]" strokeWidth={1.6} />
              </div>
              <div className="font-display text-3xl text-[#D4A853]">{s.num}</div>
            </div>
            <div className="md:col-span-7">
              <h3 className="font-display text-2xl text-[#1A1F3D]">{s.title}</h3>
              <p className="mt-3 text-[#2D2D2D] leading-relaxed">{s.body}</p>
            </div>
            <div className="md:col-span-3 flex md:justify-end">
              <Link to={s.to} className="btn-ghost inline-flex items-center gap-2 w-full md:w-auto justify-center">
                {s.cta} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
